/**
 * Inactivity Timeout Manager
 * HIPAA Compliant - Automatic logout after 30 minutes of inactivity
 * §164.312(a)(2)(iii) - Automatic Logoff (REQUIRED)
 */

import { signOut } from 'aws-amplify/auth';

class InactivityManager {
  constructor() {
    this.inactivityTimer = null;
    this.warningTimer = null;
    this.INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes (HIPAA standard)
    this.WARNING_TIME = 28 * 60 * 1000; // Show warning at 28 minutes (2 min before logout)
    this.lastActivity = Date.now();
    this.isWarningShown = false;
    this.onLogout = null;
    this.warningModal = null;
  }

  /**
   * Initialize inactivity monitoring
   * @param {Function} onLogoutCallback - Function to call when user is logged out
   */
  initialize(onLogoutCallback) {
    this.onLogout = onLogoutCallback;
    
    // Events that count as user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Reset timer on any activity
    const resetTimer = () => {
      this.lastActivity = Date.now();
      this.resetInactivityTimer();
      
      // Hide warning if shown
      if (this.isWarningShown) {
        this.hideWarning();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    // Start the timer
    this.resetInactivityTimer();
    
    console.info('[Inactivity] ✅ Inactivity monitoring initialized (30 min timeout)');
  }

  /**
   * Reset inactivity timers
   */
  resetInactivityTimer() {
    // Clear existing timers
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }

    // Set warning timer (2 minutes before logout)
    this.warningTimer = setTimeout(() => {
      this.showWarning();
    }, this.WARNING_TIME);

    // Set logout timer (15 minutes)
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivityLogout();
    }, this.INACTIVITY_TIMEOUT);
  }

  /**
   * Show warning modal before logout
   */
  showWarning() {
    if (this.isWarningShown) return;
    
    this.isWarningShown = true;
    
    // Create warning modal
    this.warningModal = document.createElement('div');
    this.warningModal.id = 'inactivity-warning-modal';
    this.warningModal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.3s ease-out;
    `;

    const timeRemaining = Math.ceil((this.INACTIVITY_TIMEOUT - this.WARNING_TIME) / 1000);

    this.warningModal.innerHTML = `
      <div style="
        background: white;
        padding: 40px;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        text-align: center;
        max-width: 450px;
        animation: slideIn 0.3s ease-out;
      ">
        <div style="font-size: 64px; margin-bottom: 20px;">⏰</div>
        <h2 style="color: #dc3545; margin: 0 0 15px 0; font-size: 24px;">
          Session Timeout Warning
        </h2>
        <p style="color: #666; margin-bottom: 20px; font-size: 16px;">
          You will be automatically logged out in <strong>${timeRemaining} seconds</strong> due to inactivity.
        </p>
        <p style="color: #999; font-size: 14px; margin-bottom: 30px;">
          This is required for HIPAA compliance to protect patient data.
        </p>
        
        <button id="stay-logged-in-btn" style="
          width: 100%;
          padding: 15px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
          margin-bottom: 10px;
        ">
          Stay Logged In
        </button>
        
        <button id="logout-now-btn" style="
          width: 100%;
          padding: 15px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
        ">
          Logout Now
        </button>
      </div>
    `;

    document.body.appendChild(this.warningModal);

    // Add event listeners to buttons
    document.getElementById('stay-logged-in-btn').addEventListener('click', () => {
      this.hideWarning();
      this.resetInactivityTimer();
    });

    document.getElementById('logout-now-btn').addEventListener('click', () => {
      this.handleInactivityLogout();
    });

    // Add animations
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideIn {
        from { transform: translateY(-50px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    console.warn('[Inactivity] ⚠️ Showing inactivity warning');
  }

  /**
   * Hide warning modal
   */
  hideWarning() {
    if (this.warningModal) {
      this.warningModal.remove();
      this.warningModal = null;
    }
    this.isWarningShown = false;
  }

  /**
   * Handle automatic logout due to inactivity
   */
  async handleInactivityLogout() {
    console.warn('[Inactivity] 🔒 Logging out due to inactivity');
    
    // Hide warning if shown
    this.hideWarning();
    
    // Show logout screen
    this.showLogoutScreen();
    
    // Log audit event
    await this.logAuditEvent('AUTO_LOGOUT_INACTIVITY');
    
    // Sign out from Cognito
    try {
      await signOut({ global: true });
    } catch (error) {
      console.error('[Inactivity] Error signing out:', error);
    }
    
    // Clear all storage
    sessionStorage.clear();
    localStorage.clear();
    
    // Call logout callback
    if (this.onLogout) {
      setTimeout(() => {
        this.onLogout();
      }, 2000);
    }
  }

  /**
   * Show logout screen
   */
  showLogoutScreen() {
    const logoutScreen = document.createElement('div');
    logoutScreen.id = 'inactivity-logout-screen';
    logoutScreen.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      z-index: 10000000;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.3s ease-out;
    `;

    logoutScreen.innerHTML = `
      <div style="
        background: white;
        padding: 50px;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        text-align: center;
        max-width: 500px;
      ">
        <div style="font-size: 72px; margin-bottom: 20px;">🔒</div>
        <h2 style="color: #333; margin: 0 0 15px 0;">Session Expired</h2>
        <p style="color: #666; margin-bottom: 10px;">
          You have been automatically logged out due to 15 minutes of inactivity.
        </p>
        <p style="color: #999; font-size: 14px; margin-bottom: 30px;">
          This is required for HIPAA compliance to protect patient health information.
        </p>
        
        <div style="
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 20px;
        ">
          <p style="color: #666; font-size: 13px; margin: 0;">
            Redirecting to login page...
          </p>
        </div>
      </div>
    `;

    document.body.appendChild(logoutScreen);
  }

  /**
   * Log audit event for HIPAA compliance
   */
  async logAuditEvent(eventType) {
    try {
      const auditData = {
        event_type: eventType,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        session_duration: Date.now() - this.lastActivity
      };

      // Send to backend audit log
      await fetch('/api/v1/security/audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(auditData)
      });
    } catch (error) {
      console.error('[Inactivity] Failed to log audit event:', error);
    }
  }

  /**
   * Get session info
   */
  getSessionInfo() {
    const now = Date.now();
    const inactiveDuration = now - this.lastActivity;
    const remainingTime = this.INACTIVITY_TIMEOUT - inactiveDuration;

    return {
      lastActivity: new Date(this.lastActivity).toISOString(),
      inactiveDuration: Math.floor(inactiveDuration / 1000), // seconds
      remainingTime: Math.floor(remainingTime / 1000), // seconds
      timeoutMinutes: this.INACTIVITY_TIMEOUT / 60000
    };
  }

  /**
   * Cleanup
   */
  cleanup() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
    this.hideWarning();
    console.info('[Inactivity] Cleaned up');
  }
}

const inactivityManager = new InactivityManager();
export default inactivityManager;
